import axios from 'axios';
import qs from 'qs';
import { Reimbursement } from '../interfaces/reimbursment';
import { StrapiResponse, StrapiResponseList } from '../interfaces/commons';

const { REACT_APP_API_URL } = process.env;

// Il filtro data passa giorni (YYYY-MM-DD): sui campi datetime un $lte sul solo
// giorno escluderebbe tutto l'ultimo giorno del range.
const endOfDay = (date?: any) =>
  typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)
    ? `${date}T23:59:59.999Z`
    : date;

const getDateRangeFilter = (
  searchParams: URLSearchParams,
  attribute: string,
) => {
  const rawFilter = searchParams.get(attribute);
  if (!rawFilter) return null;

  const selectedFilter = Object.entries(qs.parse(rawFilter)).map(
    ([, value]) => value as any,
  )[0];
  const parsedRange = qs.parse(selectedFilter?.label || '') as {
    from?: string;
    to?: string;
  };

  if (!parsedRange.from && !parsedRange.to) return null;

  return {
    ...(parsedRange.from ? { $gte: parsedRange.from } : {}),
    ...(parsedRange.to ? { $lte: endOfDay(parsedRange.to) } : {}),
  };
};

// Stessa precedenza di getReimbursmentSubmissionDate (la data mostrata in
// tabella): un campo successivo conta solo se i precedenti sono vuoti.
const getSubmissionDateFilter = (searchParams: URLSearchParams) => {
  const rangeFilter = getDateRangeFilter(searchParams, 'submittedAt');
  if (!rangeFilter) return {};

  return {
    $or: [
      { lastUserUpdateAt: rangeFilter },
      { lastUserUpdateAt: { $null: true }, submittedAt: rangeFilter },
      {
        lastUserUpdateAt: { $null: true },
        submittedAt: { $null: true },
        createdAt: rangeFilter,
      },
    ],
  };
};

export const listReimbursements = async (
  type: 'lesson' | 'course' | 'event',
  searchParams: any,
  token: string | null,
) => {
  const query = {
    populate: {
      profile: true,
      attachments: true,
      lesson: {
        populate: {
          course: {
            populate: ['section'],
          },
        },
      },
      treasurerCourse: true,
      supervisorCourse: true,
      event: {
        populate: {
          section: true,
          balance: {
            populate: ['teacherPayments', 'teacherRefunds'],
          },
        },
      },
    },
    filters: {
      $and: [
        {
          module: { $notNull: true },
        },
        // Teacher name/surname search
        searchParams.get('search')
          ? {
              $or: [
                {
                  profile: {
                    surname: { $contains: searchParams.get('search') },
                  },
                },
                {
                  profile: {
                    name: { $contains: searchParams.get('search') },
                  },
                },
                {
                  module: {
                    $containsi: searchParams.get('search'),
                  },
                },
                {
                  simplifiedFor: type,
                },
              ],
            }
          : {},
        // Course/Event title search
        searchParams.get('courseTitle') || searchParams.get('eventTitle')
          ? type === 'lesson'
            ? {
                $or: [
                  {
                    lesson: {
                      course: {
                        title: { $contains: searchParams.get('courseTitle') },
                      },
                    },
                  },
                  {
                    simplifiedFor: 'lesson',
                  },
                ],
              }
            : type === 'course'
              ? {
                  $or: [
                    {
                      treasurerCourse: {
                        title: { $contains: searchParams.get('courseTitle') },
                      },
                    },
                    {
                      supervisorCourse: {
                        title: { $contains: searchParams.get('courseTitle') },
                      },
                    },
                    {
                      simplifiedFor: 'course',
                    },
                  ],
                }
              : {
                  $or: [
                    {
                      event: {
                        title: { $contains: searchParams.get('eventTitle') },
                      },
                    },
                    {
                      simplifiedFor: 'event',
                    },
                  ],
                }
          : {},
        // Section filter
        searchParams.get('sections')
          ? type === 'lesson'
            ? {
                lesson: {
                  course: {
                    section: {
                      id: {
                        $in: Object.entries(
                          qs.parse(searchParams.get('sections')),
                        )
                          .map(([, value]) => value)
                          .map((elem: any) => elem.value),
                      },
                    },
                  },
                },
              }
            : type === 'course'
              ? {
                  $or: [
                    {
                      treasurerCourse: {
                        section: {
                          id: {
                            $in: Object.entries(
                              qs.parse(searchParams.get('sections')),
                            )
                              .map(([, value]) => value)
                              .map((elem: any) => elem.value),
                          },
                        },
                      },
                    },
                    {
                      supervisorCourse: {
                        section: {
                          id: {
                            $in: Object.entries(
                              qs.parse(searchParams.get('sections')),
                            )
                              .map(([, value]) => value)
                              .map((elem: any) => elem.value),
                          },
                        },
                      },
                    },
                  ],
                }
              : {
                  event: {
                    section: {
                      id: {
                        $in: Object.entries(
                          qs.parse(searchParams.get('sections')),
                        )
                          .map(([, value]) => value)
                          .map((elem: any) => elem.value),
                      },
                    },
                  },
                }
          : {},
        // Role filter (course only)
        type === 'course' && searchParams.get('role')
          ? {
              $or: Object.entries(qs.parse(searchParams.get('role')))
                .map(([, value]) => value)
                .map((elem: any) => {
                  if (elem.value === 'treasurer') {
                    return { treasurerCourse: { id: { $notNull: true } } };
                  }
                  return { supervisorCourse: { id: { $notNull: true } } };
                }),
            }
          : {},
        // Status filter
        searchParams.get('status')
          ? type === 'lesson'
            ? {
                $or: Object.entries(qs.parse(searchParams.get('status')))
                  .map(([, value]) => value as { value: string })
                  .flatMap((elem) => {
                    if (elem.value === 'PAID') {
                      return [
                        {
                          lesson: {
                            paymentDate: { $notNull: true },
                          },
                        },
                        {
                          simplifiedFor: 'lesson',
                        },
                      ] as any;
                    }
                    return [
                      {
                        status: elem.value,
                        lesson: {
                          paymentDate: { $null: true },
                        },
                      } as any,
                    ];
                  }),
              }
            : type === 'course'
              ? {
                  $or: Object.entries(qs.parse(searchParams.get('status')))
                    .map(([, value]) => value as { value: string })
                    .flatMap((elem) => {
                      if (elem.value === 'PAID') {
                        return [
                          {
                            treasurerCourse: {
                              treasurerPaymentDate: { $notNull: true },
                            },
                          },
                          {
                            supervisorCourse: {
                              supervisorPaymentDate: { $notNull: true },
                            },
                          },
                          {
                            simplifiedFor: 'course',
                          },
                        ] as any;
                      }
                      return [
                        {
                          status: elem.value,
                          $and: [
                            {
                              treasurerCourse: {
                                treasurerPaymentDate: { $null: true },
                              },
                            },
                            {
                              supervisorCourse: {
                                supervisorPaymentDate: { $null: true },
                              },
                            },
                          ],
                        } as any,
                      ];
                    }),
                }
              : {
                  $or: Object.entries(qs.parse(searchParams.get('status')))
                    .map(([, value]) => value as { value: string })
                    .flatMap((elem) => {
                      if (elem.value === 'PAID') {
                        return [
                          {
                            event: {
                              balance: {
                                teacherPayments: {
                                  paymentDate: { $notNull: true },
                                },
                              },
                            },
                          },
                          {
                            event: {
                              balance: {
                                teacherRefunds: {
                                  paymentDate: { $notNull: true },
                                },
                              },
                            },
                          },
                          {
                            simplifiedFor: 'event',
                          },
                        ] as any;
                      }
                      return [
                        {
                          status: elem.value,
                          event: {
                            balance: {
                              teacherPayments: {
                                paymentDate: { $null: true },
                              },
                              teacherRefunds: {
                                paymentDate: { $null: true },
                              },
                            },
                          },
                        } as any,
                      ];
                    }),
                }
          : {},
        getSubmissionDateFilter(searchParams),
        // Date filters
        searchParams.get('lessonDate') && type === 'lesson'
          ? {
              lesson: {
                date: {
                  $gte: qs.parse(
                    Object.entries(
                      qs.parse(searchParams.get('lessonDate')),
                    ).map(([, value]) => value as any)[0]?.label,
                  ).from,
                  $lte: endOfDay(
                    qs.parse(
                      Object.entries(
                        qs.parse(searchParams.get('lessonDate')),
                      ).map(([, value]) => value as any)[0]?.label,
                    ).to,
                  ),
                },
              },
            }
          : searchParams.get('courseDate') && type === 'course'
            ? {
                $or: [
                  {
                    treasurerCourse: {
                      startDate: {
                        $gte: qs.parse(
                          Object.entries(
                            qs.parse(searchParams.get('courseDate')),
                          ).map(([, value]) => value as any)[0]?.label,
                        ).from,
                        $lte: qs.parse(
                          Object.entries(
                            qs.parse(searchParams.get('courseDate')),
                          ).map(([, value]) => value as any)[0]?.label,
                        ).to,
                      },
                    },
                  },
                  {
                    supervisorCourse: {
                      startDate: {
                        $gte: qs.parse(
                          Object.entries(
                            qs.parse(searchParams.get('courseDate')),
                          ).map(([, value]) => value as any)[0]?.label,
                        ).from,
                        $lte: qs.parse(
                          Object.entries(
                            qs.parse(searchParams.get('courseDate')),
                          ).map(([, value]) => value as any)[0]?.label,
                        ).to,
                      },
                    },
                  },
                ],
              }
            : searchParams.get('eventDate') && type === 'event'
              ? {
                  event: {
                    startDate: {
                      $gte: qs.parse(
                        Object.entries(
                          qs.parse(searchParams.get('eventDate')),
                        ).map(([, value]) => value as any)[0]?.label,
                      ).from,
                      $lte: endOfDay(
                        qs.parse(
                          Object.entries(
                            qs.parse(searchParams.get('eventDate')),
                          ).map(([, value]) => value as any)[0]?.label,
                        ).to,
                      ),
                    },
                  },
                }
              : {},
        // Payment date filter
        searchParams.get('paymentDate')
          ? type === 'lesson'
            ? {
                $or: [
                  {
                    lesson: {
                      paymentDate: {
                        $gte: qs.parse(
                          Object.entries(
                            qs.parse(searchParams.get('paymentDate')),
                          ).map(([, value]) => value as any)[0]?.label,
                        ).from,
                        $lte: endOfDay(
                          qs.parse(
                            Object.entries(
                              qs.parse(searchParams.get('paymentDate')),
                            ).map(([, value]) => value as any)[0]?.label,
                          ).to,
                        ),
                      },
                    },
                  },
                  {
                    simplifiedFor: 'lesson',
                  },
                ],
              }
            : type === 'course'
              ? {
                  $or: [
                    {
                      simplifiedFor: 'course',
                    },
                    {
                      treasurerCourse: {
                        treasurerPaymentDate: {
                          $gte: qs.parse(
                            Object.entries(
                              qs.parse(searchParams.get('paymentDate')),
                            ).map(([, value]) => value as any)[0]?.label,
                          ).from,
                          $lte: qs.parse(
                            Object.entries(
                              qs.parse(searchParams.get('paymentDate')),
                            ).map(([, value]) => value as any)[0]?.label,
                          ).to,
                        },
                      },
                    },
                    {
                      supervisorCourse: {
                        supervisorPaymentDate: {
                          $gte: qs.parse(
                            Object.entries(
                              qs.parse(searchParams.get('paymentDate')),
                            ).map(([, value]) => value as any)[0]?.label,
                          ).from,
                          $lte: qs.parse(
                            Object.entries(
                              qs.parse(searchParams.get('paymentDate')),
                            ).map(([, value]) => value as any)[0]?.label,
                          ).to,
                        },
                      },
                    },
                  ],
                }
              : {
                  $or: [
                    {
                      simplifiedFor: 'event',
                    },
                    {
                      event: {
                        balance: {
                          teacherPayments: {
                            paymentDate: {
                              $gte: qs.parse(
                                Object.entries(
                                  qs.parse(searchParams.get('paymentDate')),
                                ).map(([, value]) => value as any)[0]?.label,
                              ).from,
                              $lte: qs.parse(
                                Object.entries(
                                  qs.parse(searchParams.get('paymentDate')),
                                ).map(([, value]) => value as any)[0]?.label,
                              ).to,
                            },
                          },
                        },
                      },
                    },
                    {
                      event: {
                        balance: {
                          teacherRefunds: {
                            paymentDate: {
                              $gte: qs.parse(
                                Object.entries(
                                  qs.parse(searchParams.get('paymentDate')),
                                ).map(([, value]) => value as any)[0]?.label,
                              ).from,
                              $lte: qs.parse(
                                Object.entries(
                                  qs.parse(searchParams.get('paymentDate')),
                                ).map(([, value]) => value as any)[0]?.label,
                              ).to,
                            },
                          },
                        },
                      },
                    },
                  ],
                }
          : {},
        // Type filter
        type === 'lesson'
          ? {
              $or: [
                { lesson: { id: { $notNull: true } } },
                { simplifiedFor: 'lesson' },
              ],
            }
          : type === 'course'
            ? {
                $or: [
                  { treasurerCourse: { id: { $notNull: true } } },
                  { supervisorCourse: { id: { $notNull: true } } },
                  { simplifiedFor: 'course' },
                ],
              }
            : {
                $or: [
                  { event: { id: { $notNull: true } } },
                  { simplifiedFor: 'event' },
                ],
              },
      ],
    },
    pagination: {
      page: searchParams.get('page'),
      pageSize: searchParams.get('pageSize') || 10,
    },
    sort: (() => {
      const sortParams = JSON.parse(searchParams.get('sort') ?? '{}');
      return JSON.stringify(sortParams) === '{}'
        ? { updatedAt: 'desc' }
        : sortParams;
    })(),
  };

  const { data } = await axios.get<StrapiResponseList<Reimbursement>>(
    `${REACT_APP_API_URL}/reimbursements?${qs.stringify(query)}`,
    {
      headers: { authorization: `Bearer ${token}` },
    },
  );

  const filteredData = data.data.filter((reimbursement) => {
    // Non semplificati già filtrati da backend
    if (!reimbursement.attributes.simplifiedFor) {
      return true;
    }

    // Filtri in memoria per semplificati
    const statusFilter = searchParams.get('status');
    const paymentDateFilter = searchParams.get('paymentDate');
    const searchFilter = searchParams.get('search');
    const courseTitleFilter = searchParams.get('courseTitle');

    const conditions = [];

    // Search filter for simplified reimbursements
    if (searchFilter) {
      const searchTerm = searchFilter.toLowerCase();
      const firstName =
        reimbursement.attributes.module?.personalInfo?.firstName?.toLowerCase() ||
        '';
      const lastName =
        reimbursement.attributes.module?.personalInfo?.lastName?.toLowerCase() ||
        '';

      const matchesSearch =
        firstName.includes(searchTerm) ||
        lastName.includes(searchTerm) ||
        `${firstName} ${lastName}`.includes(searchTerm);

      conditions.push(matchesSearch);
    }

    // Course title filter for simplified reimbursements
    if (courseTitleFilter) {
      const courseTerm = courseTitleFilter.toLowerCase();
      const description =
        reimbursement.attributes.module?.description?.toLowerCase() || '';

      conditions.push(description.includes(courseTerm));
    }

    if (statusFilter === 'PAID') {
      conditions.push(
        reimbursement.attributes.module.simplifiedPaymentDate !== null,
      );
    }

    if (paymentDateFilter) {
      const paymentDateFilter = qs.parse(
        Object.entries(qs.parse(searchParams.get('paymentDate') || '')).map(
          ([, value]) => value as any,
        )[0]?.label,
      );

      if (
        (paymentDateFilter.from || paymentDateFilter.to) &&
        reimbursement.attributes.module.simplifiedPaymentDate
      ) {
        const paymentDate = new Date(
          reimbursement.attributes.module.simplifiedPaymentDate,
        );

        if (paymentDateFilter.from && paymentDateFilter.to) {
          const fromDate = new Date(paymentDateFilter.from as string);
          const toDate = new Date(paymentDateFilter.to as string);
          conditions.push(paymentDate >= fromDate && paymentDate <= toDate);
        } else if (paymentDateFilter.from) {
          const fromDate = new Date(paymentDateFilter.from as string);
          conditions.push(paymentDate >= fromDate);
        } else if (paymentDateFilter.to) {
          const toDate = new Date(paymentDateFilter.to as string);
          conditions.push(paymentDate <= toDate);
        }
      }

      if (reimbursement.attributes.module.simplifiedPaymentDate === null) {
        conditions.push(false);
      }
    }

    return conditions.every((condition) => condition);
  });

  return {
    ...data,
    data: filteredData,
  };
};

export const getReimbursement = async (id: number, token: string | null) => {
  const query = {
    populate: {
      profile: {
        populate: ['*'],
      },
      attachments: true,
      lesson: {
        populate: ['course', 'course.section'],
      },
    },
  };

  const { data } = await axios.get<StrapiResponse<Reimbursement>>(
    `${REACT_APP_API_URL}/reimbursements/${id}?${qs.stringify(query)}`,
    {
      headers: { authorization: `Bearer ${token}` },
    },
  );
  return data;
};

export const updateReimbursement = async ({
  data,
  token,
  id,
}: {
  data: any;
  token: string | null;
  id: number;
}) => {
  const response = await axios.put(
    `${REACT_APP_API_URL}/reimbursements/${id}`,
    { data },
    {
      headers: { authorization: `Bearer ${token}` },
    },
  );
  return response;
};

export const downloadLessonReimbursementsCSV = async (
  searchParams: URLSearchParams,
  token: string | null,
) => {
  const query = {
    populate: {
      profile: true,
      attachments: true,
      lesson: {
        populate: {
          course: {
            populate: ['section'],
          },
        },
      },
    },
    filters: {
      $and: [
        {
          module: { $notNull: true },
        },
        // Teacher name/surname search
        searchParams.get('search')
          ? {
              $or: [
                {
                  profile: {
                    surname: { $contains: searchParams.get('search') || '' },
                  },
                },
                {
                  profile: {
                    name: { $contains: searchParams.get('search') || '' },
                  },
                },
                {
                  module: {
                    $containsi: searchParams.get('search'),
                  },
                },
                {
                  simplifiedFor: 'lesson',
                },
              ],
            }
          : {},
        // Course title search
        searchParams.get('courseTitle')
          ? {
              $or: [
                {
                  lesson: {
                    course: {
                      title: {
                        $contains: searchParams.get('courseTitle') || '',
                      },
                    },
                  },
                },
                {
                  simplifiedFor: 'lesson',
                },
              ],
            }
          : {},
        // Section filter
        searchParams.get('sections')
          ? {
              lesson: {
                course: {
                  section: {
                    id: {
                      $in: Object.entries(
                        qs.parse(searchParams.get('sections') || ''),
                      )
                        .map(([, value]) => value)
                        .map((elem: any) => elem.value),
                    },
                  },
                },
              },
            }
          : {},
        // Status filter
        searchParams.get('status')
          ? {
              $or: Object.entries(qs.parse(searchParams.get('status') || ''))
                .map(([, value]) => value)
                .flatMap((elem: any) => {
                  if (elem.value === 'PAID') {
                    return [
                      {
                        lesson: {
                          paymentDate: { $notNull: true },
                        },
                      },
                      {
                        simplifiedFor: 'lesson',
                      },
                    ] as any;
                  }
                  return [
                    {
                      status: elem.value,
                      lesson: {
                        paymentDate: { $null: true },
                      },
                    } as any,
                  ];
                }),
            }
          : {},
        // Date filters
        getSubmissionDateFilter(searchParams),
        searchParams.get('lessonDate')
          ? {
              lesson: {
                date: {
                  $gte: qs.parse(
                    Object.entries(
                      qs.parse(searchParams.get('lessonDate') || ''),
                    ).map(([, value]) => value as any)[0]?.label || '',
                  ).from,
                  $lte: endOfDay(
                    qs.parse(
                      Object.entries(
                        qs.parse(searchParams.get('lessonDate') || ''),
                      ).map(([, value]) => value as any)[0]?.label || '',
                    ).to,
                  ),
                },
              },
            }
          : {},
        // Payment date filter
        searchParams.get('paymentDate')
          ? {
              lesson: {
                paymentDate: {
                  $gte: qs.parse(
                    Object.entries(
                      qs.parse(searchParams.get('paymentDate') || ''),
                    ).map(([, value]) => value as any)[0]?.label || '',
                  ).from,
                  $lte: endOfDay(
                    qs.parse(
                      Object.entries(
                        qs.parse(searchParams.get('paymentDate') || ''),
                      ).map(([, value]) => value as any)[0]?.label || '',
                    ).to,
                  ),
                },
              },
            }
          : {},
        {
          $or: [
            { lesson: { id: { $notNull: true } } },
            { simplifiedFor: 'lesson' },
          ],
        },
      ],
    },
    sort: { updatedAt: 'desc' },
  };

  const response = await axios.get(
    `${REACT_APP_API_URL}/reimbursements/lesson-csv?${qs.stringify(query)}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      responseType: 'blob',
    },
  );

  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', 'lesson-reimbursements.csv');
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export const downloadCourseReimbursementsCSV = async (
  searchParams: URLSearchParams,
  token: string | null,
) => {
  const query = {
    populate: {
      profile: true,
      treasurerCourse: true,
      supervisorCourse: true,
    },
    filters: {
      $and: [
        {
          module: { $notNull: true },
        },
        searchParams.get('search')
          ? {
              $or: [
                {
                  profile: {
                    surname: { $contains: searchParams.get('search') || '' },
                  },
                },
                {
                  profile: {
                    name: { $contains: searchParams.get('search') || '' },
                  },
                },
                {
                  module: {
                    $containsi: searchParams.get('search'),
                  },
                },
                {
                  simplifiedFor: 'course',
                },
              ],
            }
          : {},
        // Course title search
        searchParams.get('courseTitle')
          ? {
              $or: [
                {
                  treasurerCourse: {
                    title: { $contains: searchParams.get('courseTitle') || '' },
                  },
                },
                {
                  supervisorCourse: {
                    title: { $contains: searchParams.get('courseTitle') || '' },
                  },
                },
                {
                  simplifiedFor: 'course',
                },
              ],
            }
          : {},
        // Section filter
        searchParams.get('sections')
          ? {
              $or: [
                {
                  treasurerCourse: {
                    section: {
                      id: {
                        $in: Object.entries(
                          qs.parse(searchParams.get('sections') || ''),
                        )
                          .map(([, value]) => value)
                          .map((elem: any) => elem.value),
                      },
                    },
                  },
                },
                {
                  supervisorCourse: {
                    section: {
                      id: {
                        $in: Object.entries(
                          qs.parse(searchParams.get('sections') || ''),
                        )
                          .map(([, value]) => value)
                          .map((elem: any) => elem.value),
                      },
                    },
                  },
                },
              ],
            }
          : {},
        // Role filter
        searchParams.get('role')
          ? {
              $or: Object.entries(qs.parse(searchParams.get('role') || ''))
                .map(([, value]) => value)
                .map((elem: any) => {
                  if (elem.value === 'treasurer') {
                    return { treasurerCourse: { id: { $notNull: true } } };
                  }
                  return { supervisorCourse: { id: { $notNull: true } } };
                }),
            }
          : {},
        // Status filter
        searchParams.get('status')
          ? {
              $or: Object.entries(qs.parse(searchParams.get('status') || ''))
                .map(([, value]) => value)
                .flatMap((elem: any) => {
                  if (elem.value === 'PAID') {
                    return [
                      {
                        treasurerCourse: {
                          treasurerPaymentDate: { $notNull: true },
                        },
                      },
                      {
                        supervisorCourse: {
                          supervisorPaymentDate: { $notNull: true },
                        },
                      },
                      {
                        simplifiedFor: 'course',
                      },
                    ] as any;
                  }
                  return [
                    {
                      status: elem.value,
                      $or: [
                        {
                          treasurerCourse: {
                            treasurerPaymentDate: { $null: true },
                          },
                        },
                        {
                          supervisorCourse: {
                            supervisorPaymentDate: { $null: true },
                          },
                        },
                      ],
                    } as any,
                  ];
                }),
            }
          : {},
        // Date filters
        getSubmissionDateFilter(searchParams),
        searchParams.get('courseDate')
          ? {
              $or: [
                {
                  treasurerCourse: {
                    startDate: {
                      $gte: qs.parse(
                        Object.entries(
                          qs.parse(searchParams.get('courseDate') || ''),
                        ).map(([, value]) => value as any)[0]?.label || '',
                      ).from,
                      $lte: qs.parse(
                        Object.entries(
                          qs.parse(searchParams.get('courseDate') || ''),
                        ).map(([, value]) => value as any)[0]?.label || '',
                      ).to,
                    },
                  },
                },
                {
                  supervisorCourse: {
                    startDate: {
                      $gte: qs.parse(
                        Object.entries(
                          qs.parse(searchParams.get('courseDate') || ''),
                        ).map(([, value]) => value as any)[0]?.label || '',
                      ).from,
                      $lte: qs.parse(
                        Object.entries(
                          qs.parse(searchParams.get('courseDate') || ''),
                        ).map(([, value]) => value as any)[0]?.label || '',
                      ).to,
                    },
                  },
                },
              ],
            }
          : {},
        // Payment date filter
        searchParams.get('paymentDate')
          ? {
              $or: [
                {
                  treasurerCourse: {
                    treasurerPaymentDate: {
                      $gte: qs.parse(
                        Object.entries(
                          qs.parse(searchParams.get('paymentDate') || ''),
                        ).map(([, value]) => value as any)[0]?.label || '',
                      ).from,
                      $lte: qs.parse(
                        Object.entries(
                          qs.parse(searchParams.get('paymentDate') || ''),
                        ).map(([, value]) => value as any)[0]?.label || '',
                      ).to,
                    },
                  },
                },
                {
                  supervisorCourse: {
                    supervisorPaymentDate: {
                      $gte: qs.parse(
                        Object.entries(
                          qs.parse(searchParams.get('paymentDate') || ''),
                        ).map(([, value]) => value as any)[0]?.label || '',
                      ).from,
                      $lte: qs.parse(
                        Object.entries(
                          qs.parse(searchParams.get('paymentDate') || ''),
                        ).map(([, value]) => value as any)[0]?.label || '',
                      ).to,
                    },
                  },
                },
              ],
            }
          : {},
        {
          $or: [
            { treasurerCourse: { id: { $notNull: true } } },
            { supervisorCourse: { id: { $notNull: true } } },
            { simplifiedFor: 'course' },
          ],
        },
      ],
    },
    sort: { updatedAt: 'desc' },
  };

  const response = await axios.get(
    `${REACT_APP_API_URL}/reimbursements/course-csv?${qs.stringify(query)}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      responseType: 'blob',
    },
  );

  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', 'course-reimbursements.csv');
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export const createAdminLessonReimbursement = async (
  data: FormData | object,
  token: string | null,
): Promise<StrapiResponse<Reimbursement>> => {
  const response = await axios.post(
    `${REACT_APP_API_URL}/reimbursements`,
    data,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return response.data;
};

export const createAdminCourseReimbursement = async (
  data: FormData | object,
  token: string | null,
): Promise<StrapiResponse<Reimbursement>> => {
  const response = await axios.post(
    `${REACT_APP_API_URL}/reimbursements`,
    data,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return response.data;
};

export const updateAdminLessonReimbursement = async (
  id: number,
  data: FormData | object,
  token: string | null,
): Promise<StrapiResponse<Reimbursement>> => {
  const response = await axios.put(
    `${REACT_APP_API_URL}/reimbursements/${id}`,
    data,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return response.data;
};

export const updateAdminCourseReimbursement = async (
  id: number,
  data: FormData | object,
  token: string | null,
): Promise<StrapiResponse<Reimbursement>> => {
  const response = await axios.put(
    `${REACT_APP_API_URL}/reimbursements/${id}`,
    data,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return response.data;
};

export const createAdminEventReimbursement = async (
  data: FormData | object,
  token: string | null,
): Promise<StrapiResponse<Reimbursement>> => {
  const response = await axios.post(
    `${REACT_APP_API_URL}/reimbursements`,
    data,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return response.data;
};

export const updateAdminEventReimbursement = async (
  id: number,
  data: FormData | object,
  token: string | null,
): Promise<StrapiResponse<Reimbursement>> => {
  const response = await axios.put(
    `${REACT_APP_API_URL}/reimbursements/${id}`,
    data,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return response.data;
};

export const downloadEventReimbursementsCSV = async (
  searchParams: URLSearchParams,
  token: string | null,
) => {
  const query = {
    populate: {
      profile: true,
      event: {
        populate: {
          section: true,
          balance: {
            populate: ['teacherPayments', 'teacherRefunds'],
          },
        },
      },
    },
    filters: {
      $and: [
        searchParams.get('search')
          ? {
              $or: [
                {
                  profile: {
                    surname: { $contains: searchParams.get('search') || '' },
                  },
                },
                {
                  profile: {
                    name: { $contains: searchParams.get('search') || '' },
                  },
                },
                {
                  module: {
                    $containsi: searchParams.get('search'),
                  },
                },
                {
                  simplifiedFor: 'event',
                },
              ],
            }
          : {},
        searchParams.get('eventTitle')
          ? {
              $or: [
                {
                  event: {
                    title: { $contains: searchParams.get('eventTitle') || '' },
                  },
                },
                {
                  simplifiedFor: 'event',
                },
              ],
            }
          : {},
        searchParams.get('sections')
          ? {
              event: {
                section: {
                  id: {
                    $in: Object.entries(
                      qs.parse(searchParams.get('sections') || ''),
                    )
                      .map(([, value]) => value)
                      .map((elem: any) => elem.value),
                  },
                },
              },
            }
          : {},
        searchParams.get('status')
          ? {
              $or: Object.entries(qs.parse(searchParams.get('status') || ''))
                .map(([, value]) => value)
                .flatMap((elem: any) => {
                  if (elem.value === 'PAID') {
                    return [
                      {
                        event: {
                          balance: {
                            teacherPayments: {
                              paymentDate: { $notNull: true },
                            },
                          },
                        },
                      },
                      {
                        event: {
                          balance: {
                            teacherRefunds: {
                              paymentDate: { $notNull: true },
                            },
                          },
                        },
                      },
                      {
                        simplifiedFor: 'event',
                      },
                    ] as any;
                  }
                  return [
                    {
                      status: elem.value,
                      event: {
                        balance: {
                          teacherPayments: {
                            paymentDate: { $null: true },
                          },
                          teacherRefunds: {
                            paymentDate: { $null: true },
                          },
                        },
                      },
                    } as any,
                  ];
                }),
            }
          : {},
        searchParams.get('eventDate')
          ? {
              event: {
                startDate: {
                  $gte: qs.parse(
                    Object.entries(
                      qs.parse(searchParams.get('eventDate') || ''),
                    ).map(([, value]) => value as any)[0]?.label || '',
                  ).from,
                  $lte: endOfDay(
                    qs.parse(
                      Object.entries(
                        qs.parse(searchParams.get('eventDate') || ''),
                      ).map(([, value]) => value as any)[0]?.label || '',
                    ).to,
                  ),
                },
              },
            }
          : {},
        getSubmissionDateFilter(searchParams),
        searchParams.get('paymentDate')
          ? {
              $or: [
                {
                  simplifiedFor: 'event',
                },
                {
                  event: {
                    balance: {
                      teacherPayments: {
                        paymentDate: {
                          $gte: qs.parse(
                            Object.entries(
                              qs.parse(searchParams.get('paymentDate') || ''),
                            ).map(([, value]) => value as any)[0]?.label || '',
                          ).from,
                          $lte: qs.parse(
                            Object.entries(
                              qs.parse(searchParams.get('paymentDate') || ''),
                            ).map(([, value]) => value as any)[0]?.label || '',
                          ).to,
                        },
                      },
                    },
                  },
                },
                {
                  event: {
                    balance: {
                      teacherRefunds: {
                        paymentDate: {
                          $gte: qs.parse(
                            Object.entries(
                              qs.parse(searchParams.get('paymentDate') || ''),
                            ).map(([, value]) => value as any)[0]?.label || '',
                          ).from,
                          $lte: qs.parse(
                            Object.entries(
                              qs.parse(searchParams.get('paymentDate') || ''),
                            ).map(([, value]) => value as any)[0]?.label || '',
                          ).to,
                        },
                      },
                    },
                  },
                },
              ],
            }
          : {},
        {
          $or: [
            { event: { id: { $notNull: true } } },
            { simplifiedFor: 'event' },
          ],
        },
      ],
    },
    sort: { updatedAt: 'desc' },
  };

  const response = await axios.get(
    `${REACT_APP_API_URL}/reimbursements/event-csv?${qs.stringify(query)}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      responseType: 'blob',
    },
  );

  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', 'event-reimbursements.csv');
  document.body.appendChild(link);
  link.click();
  link.remove();
};
